'use client';

import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import posthog from 'posthog-js';

import { removeDefaultProvider } from '@sats-connect/core';

import { ProtocolProfile, VaultWallet } from '@ducat-unit/client-sdk';
import useLocalStorage from '@/hooks/useLocalStorage';
import { NetworkEnum, NetworkSettingsConfig } from '@/config/config.types';
import { getConfig } from '@/config/config';
import { getWalletConnector, wait } from '@/lib/util';
import { Network } from '@/lib/networkConfig';
import { detectNetworkFromAddress } from '@/lib/networkUtils';
import { FunctionVoid } from '@/lib/connector';
import { getLogger } from '@/utils/loglevel';
import { Fee } from '@/utils/fee.utils';
import { getRandomItem } from '@/components/layout/header/network-settings/utils';
import { QuantaHeaders } from '@/hooks/api/quanta/auth';
import { useQueryClient } from '@tanstack/react-query';
import { resetQuantaQueries } from '@/hooks/api/quanta/quanta.helpers';
import { ALLOW_GUARDIAN_RANDOMIZE } from '@/constants';
import { resetWalletQueries } from '@/hooks';
import { useContractProfile } from '@/hooks/wallet/useContractProfile';
import { identifyPosthogUserByWalletAddress } from '@/components/layout/cookie-policy/cookie.utils';

import { connectWallet, reconnectWallet } from './helpers';
import { WalletInfo, WalletType } from './types';

const logger = getLogger('DucatClientCtx', 'debug');

interface DucatClientProviderProps {
  children: ReactNode;
}

interface ClientContext {
  client: DucatClientAPI;
  contract?: ProtocolProfile | null;
  isContractLoading: boolean;
  isConnected: boolean;
  walletErrorMessage: string;
  store: WalletStore;
  wallet: VaultWallet | null;
  walletType: WalletType | null;
  update_store: (store: Partial<WalletStore>) => void;
  walletAddress: string | null;
  pubkey: string | null;
  selectedNetwork: NetworkEnum;
  setSelectedNetwork: (network: NetworkEnum) => void;
  config: NetworkSettingsConfig;
  connectedNetwork: Network | 'unknown' | null;
  handleNetworkChange: (network: NetworkEnum) => void;
  isActionActive: boolean;
  setIsActionActive: Dispatch<SetStateAction<boolean>>;
  resetDrawers: boolean;
  setResetDrawers: Dispatch<SetStateAction<boolean>>;
  networkSettings: NetworkSettings;
  setNetworkSettings: (settings: NetworkSettings) => void;
  customFees: Fee[];
  setCustomFees: Dispatch<SetStateAction<Fee[]>>;
  selectedFee?: Fee;
  setSelectedFee: (fee: Fee) => void;
  quantaHeaders?: QuantaHeaders;
  setQuantaHeaders: (params: QuantaHeaders) => void;
  resetWalletError: VoidFunction;
}

export interface DucatClientAPI {
  connect: (walletType: WalletType) => Promise<void>;
  disconnect: VoidFunction;
  signMessage: (options: { address: string; message: string }) => any;
  update_store: (store: Partial<WalletStore>) => void;
  subscribeToWalletConnect: (callback: VoidFunction) => VoidFunction;
  subscribeToWalletDisconnect: (callback: VoidFunction) => VoidFunction;
}

export interface WalletStore {
  txid: string | null;
  pendingTxCompleted: boolean;
  isVaultCreationFlowInProgress: boolean;
}

export interface NetworkSettings {
  network: NetworkEnum;
  masterId: string;
  guardianUrl: string;
  guardianPublicKey: string;
  customGuardian: boolean;
}

const DEFAULT_STORE = {
  txid: null,
  pendingTxCompleted: false,
  isVaultCreationFlowInProgress: false,
};

const DEFAULT_NETWORK = NetworkEnum.MUTINYNET;

const context = createContext<ClientContext | null>(null);

export function DucatClientProvider({ children }: DucatClientProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const [store, setStore] = useState<WalletStore>(DEFAULT_STORE);
  const [walletInfo, setWalletInfo] = useLocalStorage<WalletInfo | null>('wallet-info', null);
  const [customFees, setCustomFees] = useState<Fee[]>([]);
  const [selectedFee, setSelectedFee] = useState<Fee>();
  const [wallet, setWallet] = useState<VaultWallet | null>(null);
  const [walletType, setWalletType] = useState<WalletType | null>(null);
  const [isActionActive, setIsActionActive] = useState<boolean>(false);
  const [walletErrorMessage, setWalletErrorMessage] = useState<string>('');
  const [resetDrawers, setResetDrawers] = useState<boolean>(false);
  const [quantaHeaders, setQuantaHeaders] = useLocalStorage<QuantaHeaders | null>('auth-headers', null);

  // Callback system for wallet events
  const connectCallback = useRef<VoidFunction | null>(null);
  const disconnectCallback = useRef<VoidFunction | null>(null);

  // Computed wallet props
  const walletAddress = wallet?.acct.sats.address || null;
  const pubkey = wallet?.acct.sats.pubkey || null;

  const removeListenerAccountChange = useRef<FunctionVoid | null>();
  const removeListenerNetworkChange = useRef<FunctionVoid | null>();

  // Safe localStorage initialization
  // const [selectedNetwork, setSelectedNetwork] = useLocalStorage<NetworkEnum>('selectedNetwork', DEFAULT_NETWORK);
  const selectedNetwork = DEFAULT_NETWORK;
  const setSelectedNetwork = (value: any) => {};

  const config = useMemo(() => getConfig(selectedNetwork as NetworkEnum), [selectedNetwork]);

  // const { data: contract, isLoading: isContractLoading } = useContractCtx({
  //   masterId: config.masterContractId,
  //   ordUrl: config.ordUrl,
  // });
  const { data: contract, isLoading: isContractLoading } = useContractProfile({
    masterId: config.masterContractId,
  });

  useEffect(() => {
    // If walletInfo is in LS and wallet does not exist we assume user reloaded browser and we want to auto reconnect wallet extension.
    if (!wallet && walletInfo && contract && config) {
      // console.log('AUTO RECONNECT WALLET', walletInfo);
      reconnectWallet({ walletInfo, contract, config })
        .then(onConnectWalletAccount)
        .catch((err) => console.log(`Was not able to reconnect`, err));
    }
  }, [wallet, walletInfo, contract, config]);

  const [networkSettings, setNetworkSettings] = useState<NetworkSettings>({
    network: selectedNetwork,
    masterId: config.masterContractId,
    guardianUrl: ALLOW_GUARDIAN_RANDOMIZE ? getRandomItem(config.guardians).guardianUrl : config.guardians[0]?.guardianUrl,
    guardianPublicKey: config.guardianPubkey,
    customGuardian: false,
  });

  const handleNetworkSettingsChange = (settings: NetworkSettings) => {
    setNetworkSettings(settings);

    if (settings.network !== selectedNetwork) {
      setSelectedNetwork(settings.network);
    }
  };

  const [connectedNetwork, setConnectedNetwork] = useState<Network | 'unknown' | null>(null);

  // Reset on config change (effectively on network change).
  useEffect(() => {
    setStore(DEFAULT_STORE);
    setWallet(null);
  }, [config]);

  const handleNetworkChange = async (network: NetworkEnum) => {
    if (network === selectedNetwork) return;

    disconnect();
    setSelectedNetwork(network);
  };

  const signMessage = async (options: { address: string; message: string }) => {
    const walletConnector = getWalletConnector(walletType!);
    return walletConnector.signMessage(options);
  };

  async function connect(selectedWalletType: WalletType) {
    setWalletErrorMessage('');

    if (!contract) throw Error('No contract available');
    console.log(`[connect] ${selectedWalletType}`);
    try {
      const { wallet, isValid, errorMessage } = await connectWallet({ config, contract, walletType: selectedWalletType });
      onConnectWalletAccount({ wallet, walletType: selectedWalletType, isValid, errorMessage });

      // Trigger connect callback
      if (connectCallback.current) {
        try {
          connectCallback.current();
        } catch (error) {
          console.error('[Wallet Connect Callback Error]:', error);
        }
      }
    } catch (err: any) {
      console.log(`- failed to connect to wallet. Error:`, err);
      toast.error(err.message || err);
      disconnect();
    }
  }

  function onConnectWalletAccount({
    wallet,
    walletType,
    isValid,
    errorMessage,
  }: {
    wallet: VaultWallet;
    walletType: WalletType;
    isValid: boolean;
    errorMessage: string;
  }) {
    if (!isValid) {
      setWalletErrorMessage(errorMessage);
      disconnect(walletType);
      return;
    }

    const walletConnector = getWalletConnector(walletType);
    const walletAddress = wallet.acct.sats.address;
    const walletPubkey = wallet.acct.sats.pubkey;

    setWalletType(walletType);
    setWallet(wallet);
    setWalletInfo({
      type: walletType,
      address: walletAddress,
      publicKey: walletPubkey,
      network: selectedNetwork,
    });
    setConnectedNetwork(detectNetworkFromAddress(walletAddress));
    Cookies.set('wallet', walletPubkey, { path: '/' });

    identifyPosthogUserByWalletAddress(walletAddress, walletPubkey, walletType, selectedNetwork);

    // Subscribe to account and network change:
    removeListenerAccountChange.current = walletConnector.subscribe.accountChange(disconnect);
    removeListenerNetworkChange.current = walletConnector.subscribe.networkChange(disconnect);
  }

  const disconnect = async (wallet_type?: WalletType) => {
    logger.debug(`Disconnect wallet and reset store`);
    getWalletConnector(wallet_type || walletType!)?.disconnect();

    // reset PostHog identity on wallet disconnect
    try {
      posthog.reset();
    } catch (error) {
      console.warn('[PostHog Reset] Failed', error);
    }

    // Set the wallet, walletAddress, and store to defaults
    setStore(DEFAULT_STORE);
    setWalletInfo(null);
    setWallet(null);
    setWalletType(null);
    setConnectedNetwork(null);
    removeDefaultProvider();
    setResetDrawers(true);
    setQuantaHeaders(null);

    await wait(0);

    resetWalletQueries(queryClient);
    resetQuantaQueries(queryClient);

    if (removeListenerAccountChange.current) {
      removeListenerAccountChange.current();
      removeListenerAccountChange.current = null;
    }
    if (removeListenerNetworkChange.current) {
      removeListenerNetworkChange.current();
      removeListenerNetworkChange.current = null;
    }

    // Trigger disconnect callback
    if (disconnectCallback.current) {
      try {
        disconnectCallback.current();
      } catch (error) {
        console.error('[Wallet Disconnect Callback Error]:', error);
      }
    }
  };

  const update_store = (store: Partial<WalletStore>) => {
    // Update the store with the partial data.
    setStore((e) => {
      return { ...e, ...store };
    });
  };

  function resetWalletError() {
    setWalletErrorMessage('');
  }

  // Wallet event subscription functions
  const subscribeToWalletConnect = (callback: VoidFunction) => {
    connectCallback.current = callback;

    return () => {
      connectCallback.current = null;
    };
  };

  const subscribeToWalletDisconnect = (callback: VoidFunction) => {
    disconnectCallback.current = callback;

    return () => {
      disconnectCallback.current = null;
    };
  };

  /** FIXME:
   * Performance Issue: Context Value Recreation
   *
   * Problem:
   * The context value object is recreated on every render, causing unnecessary re-renders
   * of all components consuming this context. This happens because we're creating a new
   * object in the JSX, which has a new reference each time.
   *
   * Impact:
   * - Poor performance due to unnecessary re-renders
   * - Components using useDucatClient() will re-render even if their specific
   *   dependencies haven't changed
   *
   * Solution:
   * Wrap the context value in useMemo to maintain referential equality between renders
   * when the underlying values haven't changed.
   */

  return (
    <context.Provider
      value={{
        client: {
          connect,
          disconnect,
          signMessage,
          update_store,
          subscribeToWalletConnect,
          subscribeToWalletDisconnect,
        },
        contract,
        isContractLoading,
        isConnected: wallet !== null,
        walletErrorMessage,
        resetWalletError,
        store,
        wallet,
        walletType,
        update_store,
        walletAddress,
        pubkey,
        selectedNetwork,
        setSelectedNetwork,
        config,
        connectedNetwork,
        handleNetworkChange,
        isActionActive,
        setIsActionActive,
        resetDrawers,
        setResetDrawers,
        networkSettings,
        setNetworkSettings: handleNetworkSettingsChange,
        customFees,
        setCustomFees,
        selectedFee,
        setSelectedFee,
        quantaHeaders,
        setQuantaHeaders,
      }}
    >
      {children}
    </context.Provider>
  );
}

export function useDucatClient(): ClientContext {
  const ctx = useContext(context);

  if (ctx === null) {
    throw new Error('Ducat client context is null');
  }

  return ctx;
}
